import type { TyAnchorRect, TyPopupComp } from "@/hooks/usePopupStore";
import usePopupStore from "@/hooks/usePopupStore";
import { type CSSProperties, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./styles.css";

const ANIM_DURATION = 200;

export default function PopupPaper() {
    const { comps, removeComp } = usePopupStore();
    if (comps.length === 0) return null;
    return createPortal(
        <>
            {comps.map((comp, index) => (
                <PopupLayer index={index} key={comp.name} comp={comp} onClose={() => removeComp(comp.name)} />
            ))}
        </>,
        document.body
    );
}

function PopupLayer({ comp, onClose, index }: { comp: TyPopupComp; onClose: () => void; index: number }) {
    const [state, setState] = useState<"closed" | "open">("closed");
    const isAnchor = comp.place === "anchor-below" || comp.place === "anchor-above";

    useEffect(() => {
        const id = requestAnimationFrame(() => setState("open"));
        return () => cancelAnimationFrame(id);
    }, []);

    const handleClose = () => {
        setState("closed");
        setTimeout(onClose, ANIM_DURATION);
    };

    return (
        <>
            <div
                className="popup-backdrop"
                data-cover={comp.cover}
                data-state={state}
                onClick={comp.coverClose ? handleClose : undefined}
                style={{ zIndex: `100${index}`, opacity: state === "open" ? 1 : 0 }}
            />
            {isAnchor ? (
                <AnchorPopup index={index} comp={comp} state={state} onClose={handleClose} />
            ) : (
                <ViewportPopup index={index} comp={comp} state={state} onClose={handleClose} />
            )}
        </>
    );
}

function ViewportPopup({ comp, state, onClose, index }: { comp: TyPopupComp; state: string; onClose: () => void; index: number }) {
    const alignMap: Record<string, CSSProperties> = {
        center: { alignItems: "center", justifyContent: "center" },
        top: { alignItems: "flex-start", justifyContent: "center" },
        bottom: { alignItems: "flex-end", justifyContent: "center" },
        end: { alignItems: "stretch", justifyContent: "flex-end" },
    };

    return (
        <div className="popup-viewport-wrap" style={{ ...alignMap[comp.place ?? "center"], zIndex: `100${index}` }}>
            <div className="popup-content" data-popup-anim={comp.anim} data-state={state}>
                <comp.Component onClose={onClose} details={comp.details ?? null} />
            </div>
        </div>
    );
}

function AnchorPopup({ comp, state, onClose, index }: { comp: TyPopupComp; state: string; onClose: () => void; index: number }) {
    const anchor = comp.anchor as TyAnchorRect;
    const GAP = 4;

    const style: CSSProperties = {
        minWidth: anchor.width,
    };

    if (comp.place === "anchor-below") {
        style.top = anchor.bottom + GAP;
    } else {
        style.bottom = window.innerHeight - anchor.top + GAP;
    }

    const anchorCenterX = (anchor.left + anchor.right) / 2;
    if (anchorCenterX >= window.innerWidth / 2) {
        style.right = window.innerWidth - anchor.right;
    } else {
        style.left = anchor.left;
    }

    return (
        <div className="popup-anchor-wrap" style={{ ...style, zIndex: `100${index}` }}>
            <div className="popup-content" data-popup-anim={comp.anim} data-state={state}>
                <comp.Component onClose={onClose} details={comp.details ?? null} />
            </div>
        </div>
    );
}
